import { Github } from 'lucide-react'
import { PREVIEW_APP_URL, GITHUB_REPO_URL } from '@/lib/constants'

const codeSnippet = `abstract final class GSizing {
  GSizing._();

  /// No size (0px)
  static const double none = 0.0;

  /// Single pixel size (1px)
  static const double px = 1.0;

  /// 4XS size (2px)
  static const double xs4 = 2.0;
}`

export function Hero() {
  return (
    <section className="relative overflow-hidden bg-white py-24 dark:bg-[#0A0A0B] md:py-32">
      {/* Subtle gradient background */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#448AFF]/5 via-transparent to-transparent" />

      <div className="relative mx-auto max-w-[1152px] px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left: Content */}
          <div className="flex flex-col gap-6">
            <h1 className="font-sans text-4xl font-bold leading-tight tracking-tight text-gray-900 dark:text-white md:text-5xl lg:text-[3.25rem]">
              Garmisch — a Flutter Design System built for consistent UI with
              AI.
            </h1>

            <p className="max-w-xl text-lg leading-relaxed text-gray-600 dark:text-white/70">
              Garmisch wraps Material with generated design tokens and scalable
              components so teams and AI agents can build consistent Flutter
              apps faster.
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href={PREVIEW_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-[#448AFF] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#448AFF]/90 hover:shadow-lg hover:shadow-[#448AFF]/25"
              >
                View Components
              </a>
              <a
                href={GITHUB_REPO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50 dark:border-white/20 dark:bg-white/5 dark:text-white dark:hover:border-white/30 dark:hover:bg-white/10"
              >
                <Github className="h-4 w-4" />
                GitHub
              </a>
            </div>

            {/* Note */}
            <p className="text-sm text-gray-500 dark:text-white/50">
              Open-source today. Paid plugins coming later.
            </p>
          </div>

          {/* Right: Code Snippet */}
          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 shadow-2xl dark:border-white/10 dark:bg-[#111113]">
              {/* Window Header */}
              <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-white/10">
                <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-white/20" />
                <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-white/20" />
                <div className="h-3 w-3 rounded-full bg-gray-300 dark:bg-white/20" />
                <span className="ml-3 font-mono text-xs text-gray-400 dark:text-white/40">
                  g_sizing.dart
                </span>
              </div>

              {/* Code Content */}
              <pre className="overflow-x-auto bg-white p-6 dark:bg-[#111113]">
                <code className="font-mono text-sm leading-relaxed">
                  {codeSnippet.split('\n').map((line, i) => (
                    <div key={i} className="flex">
                      <span className="mr-6 select-none text-gray-300 dark:text-white/30">
                        {String(i + 1).padStart(2, ' ')}
                      </span>
                      <span>{highlightSyntax(line)}</span>
                    </div>
                  ))}
                </code>
              </pre>
            </div>

            {/* Decorative glow */}
            <div className="pointer-events-none absolute -inset-4 -z-10 rounded-3xl bg-[#448AFF]/10 blur-3xl" />
          </div>
        </div>
      </div>
    </section>
  )
}

function highlightSyntax(line: string): React.ReactNode {
  // Comments
  if (line.trim().startsWith('///')) {
    return (
      <span className="text-emerald-600 dark:text-emerald-400/80">{line}</span>
    )
  }

  // Simple keyword highlighting
  if (line.includes('abstract final class')) {
    return (
      <>
        <span className="text-purple-600 dark:text-purple-400">
          abstract final class
        </span>
        <span className="text-[#448AFF]"> GSizing</span>
        <span className="text-gray-700 dark:text-white/90"> {'{'}</span>
      </>
    )
  }

  if (line.includes('GSizing._()')) {
    return (
      <>
        <span className="text-[#448AFF]"> GSizing</span>
        <span className="text-gray-700 dark:text-white/90">._();</span>
      </>
    )
  }

  if (line.includes('static const double')) {
    const match = line.match(/static const double (\w+) = ([\d.]+);/)
    if (match) {
      return (
        <>
          <span className="text-purple-600 dark:text-purple-400">
            {' '}
            static const double
          </span>
          <span className="text-sky-600 dark:text-sky-400"> {match[1]}</span>
          <span className="text-gray-700 dark:text-white/90"> = </span>
          <span className="text-amber-600 dark:text-amber-400">{match[2]}</span>
          <span className="text-gray-700 dark:text-white/90">;</span>
        </>
      )
    }
  }

  return <span className="text-gray-700 dark:text-white/90">{line}</span>
}
